import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authApi } from '../../api/auth';
import { errorMessage } from '../../api/client';
import { passwordProblem, useAuthRouteState } from '../../auth/route-state';
import { Alert, Button, TextField } from '../../components/ui';

/**
 * Step 2 of the reset: the token from the emailed link (or a code typed by
 * hand) plus the new password.
 *
 * The provider emails a link to STYTCH_RESET_URL with its token in the query
 * string — long and opaque, nothing anybody would type — so when `?token=` is
 * there the field is gone and the token is simply used. The manual field stays
 * for a provider that sends a code instead, and for anyone who lands here
 * without the link.
 *
 * The link carries no address, so the email is still asked for; the provider
 * checks the two against each other.
 */
export function ResetPassword() {
  const navigate = useNavigate();
  const routeState = useAuthRouteState();
  const [searchParams] = useSearchParams();
  const linkToken = searchParams.get('token')?.trim() ?? '';
  const [email, setEmail] = useState(routeState.email ?? '');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(routeState.notice ?? null);

  const reset = useMutation({
    mutationFn: async () => {
      const problem = passwordProblem(newPassword, confirm);
      if (problem) {
        setFieldError(problem);
        return;
      }
      setFieldError(null);
      await authApi.resetPassword(email.trim(), linkToken || code.trim(), newPassword);
      navigate('/sign-in', {
        replace: true,
        state: { notice: 'Your password was reset. Sign in with the new password.' },
      });
    },
  });

  const resend = useMutation({
    mutationFn: async () => {
      await authApi.forgotPassword(email.trim());
      setNotice('A new reset email was requested. Use the most recent one; it replaces the rest.');
    },
  });

  return (
    <>
      <h1 className="auth__title">Reset your password</h1>
      <p className="auth__lead">
        {linkToken
          ? 'Confirm your email address and choose a new password.'
          : 'Enter the code you received and choose a new password.'}
      </p>
      {notice ? <Alert kind="info">{notice}</Alert> : null}
      {reset.error ? <Alert kind="error">{errorMessage(reset.error)}</Alert> : null}
      {resend.error ? <Alert kind="error">{errorMessage(resend.error)}</Alert> : null}
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          reset.mutate();
        }}
        noValidate
      >
        <TextField
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        {linkToken ? null : (
          <TextField
            label="Reset code"
            name="code"
            autoComplete="one-time-code"
            required
            value={code}
            hint="From the reset email. If you followed a link from it, you do not need this."
            onChange={(e) => setCode(e.target.value)}
          />
        )}
        <TextField
          label="New password"
          name="newPassword"
          type="password"
          autoComplete="new-password"
          required
          value={newPassword}
          hint="At least 8 characters."
          onChange={(e) => setNewPassword(e.target.value)}
        />
        <TextField
          label="Confirm new password"
          name="confirmPassword"
          type="password"
          autoComplete="new-password"
          required
          value={confirm}
          error={fieldError}
          onChange={(e) => setConfirm(e.target.value)}
        />
        <Button
          type="submit"
          loading={reset.isPending}
          disabled={
            !email.trim() || (!linkToken && code.trim().length < 4) || !newPassword || !confirm
          }
        >
          Reset password
        </Button>
      </form>
      <p className="auth__switch">
        Didn&apos;t get it?{' '}
        <button
          type="button"
          className="linklike"
          onClick={() => resend.mutate()}
          disabled={!email.trim() || resend.isPending}
        >
          Send it again
        </button>
        <br />
        <Link to="/sign-in">Back to sign in</Link>
      </p>
    </>
  );
}
